import * as React from 'react';
import SearchField from '../components/SearchField';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import { toast } from 'sonner';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import useDeviceKeys from '@/src/DeviceManagement/components/hooks/useDeviceKeys';
import useDeviceTelemetry from '@/src/DeviceTelemetry/hooks/useDeviceTelemetry';
import {
    useDeviceRegistrationAutoenroll,
    DeviceRegistrationAutoenroll,
} from '@/src/DeviceManagement/components/hooks/useDeviceRegistrationAutoenroll';
import { useSoftwareReleases } from '@/src/DeviceManagement/components/hooks/useSoftwareReleases';

/*
 * Devices v2 — the fleet page: presence-first device list (online =
 * lastUsed within 3 min) with telemetry vitals and removable gallery
 * assignments, then groups (rename/delete now routed server-side),
 * enrollment (same autoenroll mechanics, honest ✓/waiting states),
 * and software downloads. Registration mechanics unchanged by
 * decision; via-group assignments are managed on the group.
 */

const ONLINE_WINDOW_MIN = 3;
const DEVICE_PAGE = 25;
const GROUP_PAGE = 10;
const REG_PAGE = 10;

interface DeviceRow {
    id: number;
    name: string;
    keyId: string;
    lastUsed?: string;
}

interface AssignmentRow {
    deviceKeyId: number;
    galleryId: number;
    galleryName?: string;
    activationId?: number;
    activationName?: string;
    isArchived?: boolean;
    viaGroupId?: number | null;
    viaGroupName?: string | null;
}

const SOFTWARE: { type: string; label: string }[] = [
    { type: 'mac-client', label: 'Mac client' },
    { type: 'smileport-adapter', label: 'Smileport adapter' },
    { type: 'mac-processing-utility', label: 'Processing utility' },
    { type: 'mac-sharing-utility', label: 'Sharing utility' },
    { type: 'mac-upload-utility', label: 'Upload utility' },
    { type: 'romr-android', label: 'ROMR (Android)' },
];

function isOnline(d: DeviceRow) {
    return (
        !!d.lastUsed &&
        moment().diff(moment(d.lastUsed), 'minutes') < ONLINE_WINDOW_MIN
    );
}

function batteryLabel(t: any): { text: string; low: boolean } | null {
    const b = t?.telemetryData?.battery;
    if (!b || b.level == null) return null;
    const raw = Number(b.level);
    if (!Number.isFinite(raw) || raw < 0) return null;
    const pct = Math.round(raw <= 1 ? raw * 100 : raw);
    const state =
        typeof b.state === 'string' && b.state.toLowerCase() === 'charging'
            ? ' charging'
            : '';
    return { text: `${pct}%${state}`, low: pct <= 20 && !state };
}

function networkLabel(t: any): string | null {
    const n = t?.telemetryData?.network;
    return n?.networkSSID || n?.connectivity || null;
}

export default function DevicesV2() {
    const navigate = useNavigate();
    const authenticatedApi = useAuthenticatedApi();
    const { orgId } = useUserOrg();
    const {
        getDeviceKeys,
        renameDeviceKey,
        destroyDeviceKey,
        generateDeviceKey,
        addDeviceKey,
    } = useDeviceKeys(orgId);
    const { listDeviceTelemetry } = useDeviceTelemetry();
    const { listDeviceRegistrations, createSingleDeviceRegistration } =
        useDeviceRegistrationAutoenroll(orgId);
    const { getLatestRelease } = useSoftwareReleases();

    const [devices, setDevices] = useState<DeviceRow[]>([]);
    const [telemetry, setTelemetry] = useState<Record<number, any>>({});
    const [assignments, setAssignments] = useState<AssignmentRow[]>([]);
    const [groups, setGroups] = useState<any[]>([]);
    const [registrations, setRegistrations] = useState<
        DeviceRegistrationAutoenroll[]
    >([]);
    const [releases, setReleases] = useState<
        { label: string; url: string; version?: string }[]
    >([]);
    const [isLoading, setIsLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [deviceLimit, setDeviceLimit] = useState(DEVICE_PAGE);
    const [groupLimit, setGroupLimit] = useState(GROUP_PAGE);
    const [regLimit, setRegLimit] = useState(REG_PAGE);
    const [renaming, setRenaming] = useState<number | null>(null);
    const [renameValue, setRenameValue] = useState('');
    const [groupRenaming, setGroupRenaming] = useState<number | null>(null);
    const [groupRenameValue, setGroupRenameValue] = useState('');
    const [newGroupOpen, setNewGroupOpen] = useState(false);
    const [newGroupName, setNewGroupName] = useState('');
    const [busy, setBusy] = useState(false);
    const [preRegOpen, setPreRegOpen] = useState(false);
    const [preReg, setPreReg] = useState({
        deviceId: '',
        deviceName: '',
        groupNames: '',
    });
    const [newKeyOpen, setNewKeyOpen] = useState(false);
    const [newKeyName, setNewKeyName] = useState('');
    const [newKeyPair, setNewKeyPair] = useState<any>(null);
    const [createdKey, setCreatedKey] = useState<any>(null);

    const openNewKey = async () => {
        setNewKeyOpen(true);
        setCreatedKey(null);
        setNewKeyName('');
        setNewKeyPair(await generateDeviceKey().catch(() => null));
    };

    const createKey = async () => {
        if (!newKeyName.trim() || !newKeyPair || busy) return;
        setBusy(true);
        const result = await addDeviceKey({
            name: newKeyName.trim(),
            keyId: newKeyPair.keyId,
            secretAccessKey: newKeyPair.secretAccessKey,
        }).catch(() => null);
        setBusy(false);
        if (result != null) {
            setCreatedKey({
                name: newKeyName.trim(),
                keyId: newKeyPair.keyId,
                secretAccessKey: newKeyPair.secretAccessKey,
            });
            setDevices(await getDeviceKeys().catch(() => devices));
        }
    };

    const deleteKey = async (d: DeviceRow) => {
        if (
            !window.confirm(
                `Delete device key "${d.name}"? The device can no longer authenticate.`,
            )
        )
            return;
        setDevices((rows) => rows.filter((r) => r.id !== d.id));
        await destroyDeviceKey(d.id).catch(async () =>
            setDevices(await getDeviceKeys().catch(() => [])),
        );
    };

    const loadGroups = async () => {
        const r = await authenticatedApi
            .get(`${orgId}/device-groups`)
            .catch(() => null);
        setGroups(r?.data ?? []);
    };

    const loadAssignments = async () => {
        const r = await authenticatedApi
            .get(`/${orgId}/device-management/assignments/org`)
            .catch(() => null);
        setAssignments(r?.data ?? []);
    };

    useEffect(() => {
        if (!orgId) return;
        let mounted = true;
        const load = async () => {
            try {
                const [keys, tele, regs] = await Promise.all([
                    getDeviceKeys().catch(() => []),
                    listDeviceTelemetry(orgId).catch(() => []),
                    listDeviceRegistrations().catch(() => []),
                    loadGroups(),
                    loadAssignments(),
                ]);
                if (!mounted) return;
                setDevices(keys ?? []);
                setRegistrations(regs ?? []);
                const byKey: Record<number, any> = {};
                (tele ?? []).forEach((t: any) => {
                    const k = t.deviceKeyId;
                    if (
                        k != null &&
                        (!byKey[k] ||
                            moment(t.updatedAt ?? t.createdAt).isAfter(
                                moment(
                                    byKey[k].updatedAt ?? byKey[k].createdAt,
                                ),
                            ))
                    ) {
                        byKey[k] = t;
                    }
                });
                setTelemetry(byKey);
            } finally {
                if (mounted) setIsLoading(false);
            }
        };
        load();
        // software is ambient — load it separately, tolerate failures
        (async () => {
            const rows: { label: string; url: string; version?: string }[] =
                [];
            for (const s of SOFTWARE) {
                const rel = await getLatestRelease(s.type as any).catch(
                    () => null,
                );
                const url = rel?.bundleUri ?? rel?.url;
                if (url)
                    rows.push({
                        label: s.label,
                        url,
                        version: rel?.version ?? rel?.versionNumber,
                    });
            }
            if (mounted) setReleases(rows);
        })();
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId]);

    const assignmentsByDevice = useMemo(() => {
        const map: Record<number, AssignmentRow[]> = {};
        assignments.forEach((a) => {
            (map[a.deviceKeyId] = map[a.deviceKeyId] ?? []).push(a);
        });
        return map;
    }, [assignments]);

    const groupsByDevice = useMemo(() => {
        const map: Record<number, string[]> = {};
        groups.forEach((g: any) => {
            (g.devices ?? []).forEach((d: any) => {
                (map[d.id] = map[d.id] ?? []).push(g.name);
            });
        });
        return map;
    }, [groups]);

    const sorted = useMemo(() => {
        const q = search.toLowerCase();
        return [...devices]
            .filter(
                (d) =>
                    !q ||
                    `${d.name} ${d.keyId}`.toLowerCase().includes(q),
            )
            .sort((a, b) => {
                const ao = isOnline(a) ? 1 : 0;
                const bo = isOnline(b) ? 1 : 0;
                if (ao !== bo) return bo - ao;
                return (
                    moment(b.lastUsed ?? 0).valueOf() -
                    moment(a.lastUsed ?? 0).valueOf()
                );
            });
    }, [devices, search]);

    const onlineCount = devices.filter(isOnline).length;

    const saveRename = async (d: DeviceRow) => {
        const name = renameValue.trim();
        setRenaming(null);
        if (!name || name === d.name) return;
        setDevices((rows) =>
            rows.map((r) => (r.id === d.id ? { ...r, name } : r)),
        );
        await renameDeviceKey(d.keyId, name).catch(() => {
            setDevices((rows) =>
                rows.map((r) =>
                    r.id === d.id ? { ...r, name: d.name } : r,
                ),
            );
        });
    };

    const removeAssignment = async (a: AssignmentRow) => {
        setAssignments((rows) =>
            rows.filter(
                (r) =>
                    !(
                        r.deviceKeyId === a.deviceKeyId &&
                        r.galleryId === a.galleryId &&
                        !r.viaGroupId
                    ),
            ),
        );
        await authenticatedApi
            .delete(
                `/${orgId}/device-management/assignments/device/${a.deviceKeyId}/gallery/${a.galleryId}`,
            )
            .catch(() => loadAssignments());
    };

    const clearAssignments = async (deviceKeyId: number) => {
        const direct = (assignmentsByDevice[deviceKeyId] ?? []).filter(
            (a) => !a.viaGroupId,
        );
        if (
            !window.confirm(
                `Remove ${direct.length} assignment${
                    direct.length === 1 ? '' : 's'
                } from this device?`,
            )
        )
            return;
        setAssignments((rows) =>
            rows.filter(
                (r) => !(r.deviceKeyId === deviceKeyId && !r.viaGroupId),
            ),
        );
        await authenticatedApi
            .delete(
                `/${orgId}/device-management/assignments/device/${deviceKeyId}`,
            )
            .catch(() => loadAssignments());
    };

    const saveGroupRename = async (g: any) => {
        const name = groupRenameValue.trim();
        setGroupRenaming(null);
        if (!name || name === g.name) return;
        setGroups((rows) =>
            rows.map((r) => (r.id === g.id ? { ...r, name } : r)),
        );
        await authenticatedApi
            .put(`/${orgId}/device-groups/${g.id}/rename`, { name })
            .catch(() => loadGroups());
    };

    const deleteGroup = async (g: any) => {
        const n = g.devices?.length ?? 0;
        if (
            !window.confirm(
                `Delete "${g.name}"?\n\n${n} device${
                    n === 1 ? '' : 's'
                } will leave the group and its gallery assignments will be removed. Devices themselves are not deleted.`,
            )
        )
            return;
        setGroups((rows) => rows.filter((r) => r.id !== g.id));
        await authenticatedApi
            .delete(`/${orgId}/device-groups/${g.id}`)
            .catch(() => loadGroups());
        loadAssignments();
    };

    const createGroup = async () => {
        const name = newGroupName.trim();
        if (!name || busy) return;
        setBusy(true);
        await authenticatedApi
            .post(`${orgId}/device-groups/create`, JSON.stringify({ name }))
            .catch(() => null);
        setNewGroupName('');
        setNewGroupOpen(false);
        await loadGroups();
        setBusy(false);
    };

    const submitPreReg = async () => {
        if (!preReg.deviceId.trim() || !preReg.deviceName.trim() || busy)
            return;
        setBusy(true);
        try {
            await createSingleDeviceRegistration({
                deviceId: preReg.deviceId.trim(),
                deviceName: preReg.deviceName.trim(),
                groupNames: preReg.groupNames.trim() || undefined,
            });
            setRegistrations(await listDeviceRegistrations());
            setPreReg({ deviceId: '', deviceName: '', groupNames: '' });
            setPreRegOpen(false);
        } catch {
            toast.error('Pre-registration failed — try again.');
        } finally {
            setBusy(false);
        }
    };

    const deleteRegistration = async (r: DeviceRegistrationAutoenroll) => {
        if (!window.confirm(`Remove pre-registration for ${r.deviceName}?`))
            return;
        setRegistrations((rows) => rows.filter((x) => x.id !== r.id));
        await authenticatedApi
            .delete(`${orgId}/device-registration-autoenroll/${r.id}`)
            .catch(async () =>
                setRegistrations(await listDeviceRegistrations()),
            );
    };

    if (isLoading) {
        return (
            <main className="mx-auto max-w-[1020px] px-[clamp(18px,3vw,34px)] pt-4">
                {[0, 1, 2].map((i) => (
                    <div
                        key={i}
                        className="mb-4 h-14 animate-pulse rounded-[12px] bg-secondary"
                    />
                ))}
            </main>
        );
    }

    return (
        <main className="mx-auto max-w-[1020px] px-[clamp(18px,3vw,34px)] pt-4">
            <div className="ps-label mb-[18px]">Devices</div>

            {/* FLEET */}
            <section className="mb-9">
                <div className="mb-3.5 flex items-center gap-3">
                    <span className="text-[13px] text-muted-foreground">
                        <b className="font-semibold text-foreground">
                            {devices.length} device
                            {devices.length === 1 ? '' : 's'}
                        </b>
                        {' · '}
                        <span
                            className={
                                onlineCount > 0
                                    ? 'font-semibold text-good'
                                    : ''
                            }
                        >
                            {onlineCount} online now
                        </span>
                    </span>
                    <SearchField
                        className="ml-auto w-[190px]"
                        placeholder="Search devices…"
                        value={search}
                        onChange={(value) => {
                            setSearch(value);
                            setDeviceLimit(DEVICE_PAGE);
                        }}
                    />
                    <button
                        type="button"
                        onClick={openNewKey}
                        className="ps-pill cursor-pointer px-3.5 font-[inherit] text-[12px] font-semibold text-foreground"
                    >
                        New key
                    </button>
                </div>
                {newKeyOpen && (
                    <div className="mb-3 rounded-[12px] border border-border p-3.5">
                        {createdKey ? (
                            <div className="text-[12.5px]">
                                <b className="font-semibold">
                                    {createdKey.name}
                                </b>{' '}
                                created — save the secret now, it isn't shown
                                again.
                                <div className="mt-1.5 font-mono text-[11px] text-muted-foreground">
                                    keyId: {createdKey.keyId}
                                    <br />
                                    secret: {createdKey.secretAccessKey}
                                </div>
                                <div className="mt-2 flex gap-3.5 text-[12px] font-semibold">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const blob = new Blob(
                                                [
                                                    JSON.stringify(
                                                        createdKey,
                                                        null,
                                                        2,
                                                    ),
                                                ],
                                                { type: 'application/json' },
                                            );
                                            const url =
                                                URL.createObjectURL(blob);
                                            const a =
                                                document.createElement('a');
                                            a.href = url;
                                            a.download = `${createdKey.name}_device_key.json`;
                                            a.click();
                                            URL.revokeObjectURL(url);
                                        }}
                                        className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] font-semibold text-brand-ink"
                                    >
                                        Download JSON
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setNewKeyOpen(false)}
                                        className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] font-semibold text-muted-foreground"
                                    >
                                        Done
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-wrap items-center gap-2.5">
                                <input
                                    autoFocus
                                    className="ps-input w-[200px] text-[13px]"
                                    placeholder="Device name"
                                    value={newKeyName}
                                    onChange={(e) =>
                                        setNewKeyName(e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') createKey();
                                        if (e.key === 'Escape')
                                            setNewKeyOpen(false);
                                    }}
                                />
                                <button
                                    type="button"
                                    disabled={
                                        busy ||
                                        !newKeyName.trim() ||
                                        !newKeyPair
                                    }
                                    onClick={createKey}
                                    className="cursor-pointer rounded-full border-0 bg-brand px-4 py-1.5 font-[inherit] text-[12px] font-semibold text-brand-foreground disabled:opacity-50"
                                >
                                    {newKeyPair
                                        ? 'Create key'
                                        : 'Generating…'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setNewKeyOpen(false)}
                                    className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-muted-foreground"
                                >
                                    Cancel
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {sorted.length === 0 && (
                    <p className="py-3 text-[13px] text-muted-foreground">
                        {devices.length === 0
                            ? 'No devices registered yet.'
                            : 'No devices match.'}
                    </p>
                )}

                {sorted.slice(0, deviceLimit).map((d, i) => {
                    const online = isOnline(d);
                    const t = telemetry[d.id];
                    const bat = batteryLabel(t);
                    const net = networkLabel(t);
                    const model = t?.deviceModel;
                    const rows = assignmentsByDevice[d.id] ?? [];
                    const direct = rows.filter((a) => !a.viaGroupId);
                    const viaGroups = Array.from(
                        new Set(
                            rows
                                .filter((a) => a.viaGroupId)
                                .map((a) => a.viaGroupName),
                        ),
                    );
                    const grpNames = groupsByDevice[d.id] ?? [];
                    return (
                        <div
                            key={d.id}
                            className={
                                'group flex flex-wrap items-start gap-[13px] py-[13px] ' +
                                (i > 0 ? 'border-t border-line' : '')
                            }
                        >
                            {/* status column — kit "Live elements" pattern */}
                            <span className="mt-[3px] flex w-[72px] flex-none items-center gap-2">
                                <span
                                    className={
                                        'h-2 w-2 flex-none rounded-full ' +
                                        (online ? 'ps-pulse' : 'bg-off')
                                    }
                                />
                                {online && (
                                    <span className="font-mono text-[10px] font-semibold tracking-[0.1em] text-good">
                                        ONLINE
                                    </span>
                                )}
                            </span>
                            <div className="min-w-0 flex-1 basis-[170px] sm:flex-none sm:basis-auto sm:w-[230px]">
                                {renaming === d.id ? (
                                    <input
                                        autoFocus
                                        className="ps-input w-full text-[13.5px] font-semibold"
                                        value={renameValue}
                                        onChange={(e) =>
                                            setRenameValue(e.target.value)
                                        }
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter')
                                                saveRename(d);
                                            if (e.key === 'Escape')
                                                setRenaming(null);
                                        }}
                                        onBlur={() => saveRename(d)}
                                    />
                                ) : (
                                    <div
                                        className={
                                            'flex items-center gap-1.5 text-[13.5px] font-semibold ' +
                                            (online ? '' : 'text-faint')
                                        }
                                    >
                                        <span className="truncate">
                                            {d.name}
                                        </span>
                                        <button
                                            type="button"
                                            title="Rename"
                                            onClick={() => {
                                                setRenaming(d.id);
                                                setRenameValue(d.name);
                                            }}
                                            className="cursor-pointer border-0 bg-transparent p-0 text-[11px] text-faint opacity-0 focus-visible:opacity-100 group-hover:opacity-100"
                                        >
                                            ✎
                                        </button>
                                    </div>
                                )}
                                <div
                                    title={d.keyId}
                                    className="mt-px truncate font-mono text-[10.5px] text-faint"
                                >
                                    {d.keyId}
                                </div>
                            </div>

                            <div className="min-w-0 flex-1 basis-full sm:basis-auto">
                                <div className="flex items-center gap-4">
                                    <div
                                        className={
                                            'flex min-w-0 flex-wrap gap-x-4 gap-y-1 text-[12px] ' +
                                            (online
                                                ? 'text-muted-foreground'
                                                : 'text-faint')
                                        }
                                    >
                                        {bat && (
                                            <span
                                                className={
                                                    bat.low && online
                                                        ? 'font-semibold text-attention'
                                                        : ''
                                                }
                                            >
                                                {online
                                                    ? bat.text
                                                    : `last known ${bat.text}`}
                                            </span>
                                        )}
                                        {online && net && <span>{net}</span>}
                                        {model && <span>{model}</span>}
                                    </div>
                                </div>
                                {grpNames.length > 0 && (
                                    <div className="mt-[7px] flex flex-wrap items-center gap-1.5">
                                        <span className="mr-0.5 font-mono text-[9px] uppercase tracking-[0.1em] text-faint">
                                            Groups
                                        </span>
                                        {grpNames.map((gn) => (
                                            <span
                                                key={gn}
                                                className="whitespace-nowrap rounded-full bg-secondary px-2.5 py-[3px] text-[11px] font-semibold text-muted-foreground"
                                            >
                                                {gn}
                                            </span>
                                        ))}
                                    </div>
                                )}
                                <div className="mt-[7px] flex flex-wrap items-center gap-1.5">
                                    <span className="mr-0.5 font-mono text-[9px] uppercase tracking-[0.1em] text-faint">
                                        Assigned
                                    </span>
                                    {direct.length === 0 &&
                                        viaGroups.length === 0 && (
                                            <span className="text-[11.5px] text-faint">
                                                nowhere — ready for the next
                                                event
                                            </span>
                                        )}
                                    {direct.map((a) => (
                                        <span
                                            key={a.galleryId}
                                            className="inline-flex items-center gap-1 rounded-full border border-border py-[3px] pl-2.5 pr-1.5 text-[11.5px] font-semibold text-muted-foreground"
                                        >
                                            {a.activationName ??
                                                a.galleryName ??
                                                `gallery ${a.galleryId}`}
                                            <button
                                                type="button"
                                                title="Remove assignment"
                                                onClick={() =>
                                                    removeAssignment(a)
                                                }
                                                className="cursor-pointer rounded-full border-0 bg-transparent px-1 text-[12px] leading-none text-faint hover:bg-secondary hover:text-attention"
                                            >
                                                ×
                                            </button>
                                        </span>
                                    ))}
                                    {viaGroups.map((gn) => (
                                        <span
                                            key={gn ?? ''}
                                            title={`Assigned through ${gn} — manage on the group`}
                                            className="inline-flex items-center rounded-full border border-dashed border-border px-2.5 py-[3px] text-[11.5px] text-faint"
                                        >
                                            <i className="mr-1 not-italic">
                                                via
                                            </i>
                                            {gn}
                                        </span>
                                    ))}
                                    {direct.length > 1 && (
                                        <button
                                            type="button"
                                            title="Remove all direct assignments"
                                            onClick={() =>
                                                clearAssignments(d.id)
                                            }
                                            className="ml-1 cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[11px] font-semibold text-faint hover:text-attention"
                                        >
                                            Clear all
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="ml-auto flex flex-none items-center gap-3.5 pt-0.5">
                                {/* wide enough for "a few seconds ago" —
                                    nowrap overflows a tighter box onto the
                                    links */}
                                <span className="w-[108px] whitespace-nowrap text-right text-[11.5px] text-faint">
                                    {d.lastUsed
                                        ? moment(d.lastUsed).fromNow(true) +
                                          ' ago'
                                        : 'never'}
                                </span>
                                {online && (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            navigate(
                                                `/orgs/${orgId}/devices/${d.keyId}/remote`,
                                            )
                                        }
                                        className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-brand-ink"
                                    >
                                        Remote
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() =>
                                        navigate(
                                            `/orgs/${orgId}/device-telemetry/${
                                                telemetry[d.id]?.deviceId ??
                                                d.keyId
                                            }`,
                                        )
                                    }
                                    className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-brand-ink"
                                >
                                    Telemetry
                                </button>
                                <button
                                    type="button"
                                    title="Delete device key"
                                    onClick={() => deleteKey(d)}
                                    className="cursor-pointer border-0 bg-transparent p-0 text-[13px] text-faint opacity-0 hover:text-attention focus-visible:opacity-100 group-hover:opacity-100"
                                >
                                    ×
                                </button>
                            </div>
                        </div>
                    );
                })}
                {sorted.length > deviceLimit && (
                    <div className="flex items-center justify-between border-t border-line py-3">
                        <span className="text-[12px] text-faint">
                            {Math.min(deviceLimit, sorted.length)} of{' '}
                            {sorted.length}
                        </span>
                        <button
                            type="button"
                            onClick={() =>
                                setDeviceLimit((l) => l + DEVICE_PAGE)
                            }
                            className="ps-pill cursor-pointer px-4 font-[inherit] text-[12.5px] font-semibold text-foreground"
                        >
                            Show more
                        </button>
                    </div>
                )}
            </section>

            {/* GROUPS */}
            <section className="mb-9">
                <div className="ps-label mb-1">Groups</div>
                {groups.length === 0 && (
                    <p className="py-2 text-[13px] text-muted-foreground">
                        No groups yet.
                    </p>
                )}
                {groups.slice(0, groupLimit).map((g: any, i: number) => (
                    <div
                        key={g.id}
                        className={
                            'flex items-center gap-3 py-[11px] ' +
                            (i > 0 ? 'border-t border-line' : '')
                        }
                    >
                        {groupRenaming === g.id ? (
                            <input
                                autoFocus
                                className="ps-input w-[220px] text-[13px] font-semibold"
                                value={groupRenameValue}
                                onChange={(e) =>
                                    setGroupRenameValue(e.target.value)
                                }
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') saveGroupRename(g);
                                    if (e.key === 'Escape')
                                        setGroupRenaming(null);
                                }}
                                onBlur={() => saveGroupRename(g)}
                            />
                        ) : (
                            <span className="text-[13px] font-semibold">
                                {g.name}
                            </span>
                        )}
                        <span className="text-[11.5px] text-faint">
                            {(g.devices ?? []).length} device
                            {(g.devices ?? []).length === 1 ? '' : 's'}
                            {(g.invites ?? []).length > 0 &&
                                ` · ${g.invites.length} invite${
                                    g.invites.length === 1 ? '' : 's'
                                }`}
                        </span>
                        <span className="ml-auto flex gap-3">
                            <button
                                type="button"
                                onClick={() =>
                                    navigate(
                                        `/device-management/device-groups/${g.id}/device-keys`,
                                    )
                                }
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-brand-ink"
                            >
                                Devices
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setGroupRenaming(g.id);
                                    setGroupRenameValue(g.name);
                                }}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-brand-ink"
                            >
                                Rename
                            </button>
                            <button
                                type="button"
                                onClick={() => deleteGroup(g)}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-faint hover:text-attention"
                            >
                                Delete
                            </button>
                        </span>
                    </div>
                ))}
                {groups.length > groupLimit && (
                    <div className="flex items-center justify-between border-t border-line py-3">
                        <span className="text-[12px] text-faint">
                            {groupLimit} of {groups.length}
                        </span>
                        <button
                            type="button"
                            onClick={() =>
                                setGroupLimit((l) => l + GROUP_PAGE)
                            }
                            className="ps-pill cursor-pointer px-4 font-[inherit] text-[12.5px] font-semibold text-foreground"
                        >
                            Show more
                        </button>
                    </div>
                )}
                {newGroupOpen ? (
                    <div className="mt-2.5 flex items-center gap-2.5">
                        <input
                            autoFocus
                            className="ps-input w-[220px] text-[13px]"
                            placeholder="Group name"
                            value={newGroupName}
                            onChange={(e) => setNewGroupName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') createGroup();
                                if (e.key === 'Escape')
                                    setNewGroupOpen(false);
                            }}
                        />
                        <button
                            type="button"
                            disabled={busy || !newGroupName.trim()}
                            onClick={createGroup}
                            className="cursor-pointer rounded-full border-0 bg-brand px-4 py-1.5 font-[inherit] text-[12px] font-semibold text-brand-foreground disabled:opacity-50"
                        >
                            Create
                        </button>
                        <button
                            type="button"
                            onClick={() => setNewGroupOpen(false)}
                            className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-muted-foreground"
                        >
                            Cancel
                        </button>
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={() => setNewGroupOpen(true)}
                        className="mt-2.5 cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-brand-ink"
                    >
                        + New group
                    </button>
                )}
            </section>

            {/* ENROLLMENT */}
            <section className="mb-9">
                <div className="ps-label mb-1">Enrollment</div>
                <p className="mb-2.5 max-w-[56ch] text-[12.5px] text-muted-foreground">
                    Pre-register a serial and the device enrolls itself on
                    first launch — landing in its groups with its name set.
                </p>
                {registrations.slice(0, regLimit).map((r, i) => (
                    <div
                        key={r.id}
                        className={
                            'group flex items-center gap-3 py-[9px] text-[12.5px] ' +
                            (i > 0 ? 'border-t border-line' : '')
                        }
                    >
                        <span className="font-mono text-[11.5px]">
                            {r.deviceId}
                        </span>
                        <span className="font-semibold">{r.deviceName}</span>
                        {r.groupNames && (
                            <span className="text-[11.5px] text-faint">
                                {r.groupNames}
                            </span>
                        )}
                        <span className="ml-auto flex items-center gap-2.5">
                            {r.counter > 0 ? (
                                <span className="text-[11px] font-semibold text-good">
                                    ✓ enrolled
                                </span>
                            ) : (
                                <span className="text-[11px] text-faint">
                                    waiting
                                </span>
                            )}
                            <button
                                type="button"
                                title="Remove pre-registration"
                                onClick={() => deleteRegistration(r)}
                                className="cursor-pointer border-0 bg-transparent p-0 text-[12px] text-faint opacity-0 hover:text-attention focus-visible:opacity-100 group-hover:opacity-100"
                            >
                                ×
                            </button>
                        </span>
                    </div>
                ))}
                {registrations.length > regLimit && (
                    <div className="flex items-center justify-between border-t border-line py-3">
                        <span className="text-[12px] text-faint">
                            {regLimit} of {registrations.length}
                        </span>
                        <button
                            type="button"
                            onClick={() => setRegLimit((l) => l + REG_PAGE)}
                            className="ps-pill cursor-pointer px-4 font-[inherit] text-[12.5px] font-semibold text-foreground"
                        >
                            Show more
                        </button>
                    </div>
                )}
                {preRegOpen ? (
                    <div className="mt-3 flex flex-wrap items-end gap-x-4 gap-y-2">
                        <div>
                            <label className="mb-1 block text-[11px] font-semibold text-muted-foreground">
                                Serial
                            </label>
                            <input
                                autoFocus
                                className="ps-input w-[160px] font-mono text-[12px]"
                                value={preReg.deviceId}
                                onChange={(e) =>
                                    setPreReg((p) => ({
                                        ...p,
                                        deviceId: e.target.value,
                                    }))
                                }
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-[11px] font-semibold text-muted-foreground">
                                Device name
                            </label>
                            <input
                                className="ps-input w-[180px] text-[12.5px]"
                                value={preReg.deviceName}
                                onChange={(e) =>
                                    setPreReg((p) => ({
                                        ...p,
                                        deviceName: e.target.value,
                                    }))
                                }
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-[11px] font-semibold text-muted-foreground">
                                Groups{' '}
                                <span className="font-normal text-faint">
                                    — comma separated, optional
                                </span>
                            </label>
                            <input
                                className="ps-input w-[180px] text-[12.5px]"
                                value={preReg.groupNames}
                                onChange={(e) =>
                                    setPreReg((p) => ({
                                        ...p,
                                        groupNames: e.target.value,
                                    }))
                                }
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') submitPreReg();
                                }}
                            />
                        </div>
                        <div className="flex items-center gap-2.5">
                            <button
                                type="button"
                                disabled={
                                    busy ||
                                    !preReg.deviceId.trim() ||
                                    !preReg.deviceName.trim()
                                }
                                onClick={submitPreReg}
                                className="cursor-pointer rounded-full border-0 bg-brand px-4 py-1.5 font-[inherit] text-[12px] font-semibold text-brand-foreground disabled:opacity-50"
                            >
                                {busy ? 'Saving…' : 'Pre-register'}
                            </button>
                            <button
                                type="button"
                                onClick={() => setPreRegOpen(false)}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-muted-foreground"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="mt-3 flex items-center gap-3.5">
                        <button
                            type="button"
                            onClick={() => setPreRegOpen(true)}
                            className="cursor-pointer rounded-full border border-border bg-transparent px-3.5 py-[7px] font-[inherit] text-[12.5px] font-semibold text-foreground hover:border-faint"
                        >
                            Pre-register a device
                        </button>
                        <button
                            type="button"
                            onClick={() =>
                                navigate('/device-management#autoenrollment')
                            }
                            className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-brand-ink"
                        >
                            Import CSV (classic) →
                        </button>
                    </div>
                )}
            </section>

            {/* SOFTWARE */}
            {releases.length > 0 && (
                <section className="mb-9">
                    <div className="ps-label mb-2">Software</div>
                    <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-[12.5px]">
                        {releases.map((r) => (
                            <span key={r.label}>
                                <a
                                    href={r.url}
                                    className="font-semibold text-brand-ink no-underline hover:underline"
                                >
                                    {r.label}
                                </a>
                                {r.version && (
                                    <span className="ml-1.5 font-mono text-[10.5px] text-faint">
                                        {r.version}
                                    </span>
                                )}
                            </span>
                        ))}
                    </div>
                </section>
            )}
        </main>
    );
}
